/*
 * Citera — global.js
 * Small, dependency-free UI layer: modal drawers (mobile nav + cart) and an
 * AJAX cart backed by Shopify's Cart + Section Rendering APIs. Deferred, no framework.
 */
(function () {
  "use strict";

  var CART_DRAWER_ID = "cart-drawer"; // the <dialog> + the section name requested
  var CART_CONTENT_ID = "cart-drawer-content"; // inner region swapped on every cart change
  var cartDrawerUpdateQueue = Promise.resolve();

  function findWithin(root, selector) {
    var matches = [];
    if (root && root.matches && root.matches(selector)) matches.push(root);
    if (root && root.querySelectorAll) {
      matches = matches.concat(Array.prototype.slice.call(root.querySelectorAll(selector)));
    }
    return matches;
  }

  /* ---------- Accessibility helpers ---------- */
  var strings = window.CiteraStrings || {};
  // Polite live region for status messages (cart updates, errors).
  var liveRegion;
  var liveRegionTimer;
  function announce(message) {
    if (!message) return;
    if (!liveRegion) {
      liveRegion = document.createElement("p");
      liveRegion.className = "visually-hidden";
      liveRegion.setAttribute("role", "status");
      liveRegion.setAttribute("aria-live", "polite");
      document.body.appendChild(liveRegion);
    }
    if (liveRegionTimer) window.clearTimeout(liveRegionTimer);
    liveRegion.textContent = "";
    liveRegionTimer = window.setTimeout(function () {
      liveRegion.textContent = message;
      liveRegionTimer = null;
    }, 60);
  }

  function setPanelExpanded(panel, expanded) {
    if (!panel || !panel.id) return;
    document.querySelectorAll("[aria-controls]").forEach(function (control) {
      if (control.getAttribute("aria-controls") === panel.id) {
        control.setAttribute("aria-expanded", expanded ? "true" : "false");
      }
    });
  }

  function focusRevealedPanel(panel) {
    if (!panel) return;
    var target = panel.querySelector(
      '[aria-invalid="true"], [data-panel-focus], input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [href]',
    );
    if (target && typeof target.focus === "function") target.focus();
  }

  var addressPanelInvokers = new WeakMap();
  function toggleAddressPanel(panel, invoker) {
    if (!panel) return;
    var opening = panel.hidden;
    panel.hidden = !opening;
    setPanelExpanded(panel, opening);

    if (opening) {
      addressPanelInvokers.set(panel, invoker);
      focusRevealedPanel(panel);
      return;
    }

    var launcher = addressPanelInvokers.get(panel);
    addressPanelInvokers.delete(panel);
    if (launcher && typeof launcher.focus === "function" && document.contains(launcher)) {
      launcher.focus();
    }
  }

  // Stop rich media when it is hidden or leaves a modal. Resetting an external
  // video iframe is the reliable provider-agnostic way to stop YouTube/Vimeo
  // audio without loading either provider's JavaScript API.
  function stopMedia(root) {
    if (!root) return;
    root.querySelectorAll("video").forEach(function (video) {
      video.pause();
    });
    root.querySelectorAll("iframe").forEach(function (frame) {
      var src = frame.getAttribute("src");
      if (src) frame.setAttribute("src", src);
    });
    root.querySelectorAll("model-viewer").forEach(function (viewer) {
      if (typeof viewer.pause === "function") viewer.pause();
    });
  }

  /* ---------- Drawers (native <dialog>) ---------- */
  // Remember what opened each dialog so focus returns there on close (WCAG 2.4.3).
  var dialogInvoker = new WeakMap();
  var dialogFallbackState = new WeakMap();
  var DIALOG_FOCUSABLE_SELECTOR =
    'a[href], area[href], input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), details > summary:first-of-type, [tabindex]:not([tabindex="-1"])';

  function getDialogFocusable(dialog) {
    return Array.prototype.slice
      .call(dialog.querySelectorAll(DIALOG_FOCUSABLE_SELECTOR))
      .filter(function (element) {
        return (
          !element.hidden &&
          element.getAttribute("aria-hidden") !== "true" &&
          element.getAttribute("tabindex") !== "-1" &&
          !(element.closest && element.closest('[hidden], [inert], [aria-hidden="true"]'))
        );
      });
  }

  function getDialogBackgroundBranches(dialog) {
    var branches = [];
    var current = dialog;
    while (current && current.parentElement) {
      var parent = current.parentElement;
      Array.prototype.forEach.call(parent.children, function (sibling) {
        if (sibling !== current) branches.push(sibling);
      });
      if (parent === document.body) break;
      current = parent;
    }
    return branches;
  }

  function activateDialogFallback(dialog) {
    var inerted = [];
    getDialogBackgroundBranches(dialog).forEach(function (element) {
      if (!element.hasAttribute("inert")) {
        element.setAttribute("inert", "");
        inerted.push(element);
      }
    });

    var previousAriaModal = dialog.getAttribute("aria-modal");
    var addedTabindex = false;
    dialog.setAttribute("aria-modal", "true");
    if (!getDialogFocusable(dialog).length && !dialog.hasAttribute("tabindex")) {
      dialog.setAttribute("tabindex", "-1");
      addedTabindex = true;
    }

    function handleFallbackKeydown(event) {
      if (event.key === "Escape") {
        event.preventDefault();
        closeDialog(dialog);
        return;
      }
      if (event.key !== "Tab") return;

      var focusable = getDialogFocusable(dialog);
      if (!focusable.length) {
        event.preventDefault();
        dialog.focus();
        return;
      }
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (
        event.shiftKey &&
        (document.activeElement === first || !dialog.contains(document.activeElement))
      ) {
        event.preventDefault();
        last.focus();
      } else if (
        !event.shiftKey &&
        (document.activeElement === last || !dialog.contains(document.activeElement))
      ) {
        event.preventDefault();
        first.focus();
      }
    }

    dialog.addEventListener("keydown", handleFallbackKeydown);
    dialogFallbackState.set(dialog, {
      inerted: inerted,
      previousAriaModal: previousAriaModal,
      addedTabindex: addedTabindex,
      handleKeydown: handleFallbackKeydown,
    });
  }

  function cleanupDialogFallback(dialog) {
    var state = dialogFallbackState.get(dialog);
    if (!state) return;
    state.inerted.forEach(function (element) {
      element.removeAttribute("inert");
    });
    dialog.removeEventListener("keydown", state.handleKeydown);
    if (state.previousAriaModal === null) dialog.removeAttribute("aria-modal");
    else dialog.setAttribute("aria-modal", state.previousAriaModal);
    if (state.addedTabindex) dialog.removeAttribute("tabindex");
    dialog.classList.remove("dialog--fallback");
    dialogFallbackState.delete(dialog);
  }

  function openDialog(dialog, invoker) {
    if (!dialog || dialog.open) return;
    dialogInvoker.set(dialog, invoker || document.activeElement);
    // The facet drawer is inline filters (role=group) on desktop; it is a real
    // modal dialog only while open on mobile.
    if (dialog.id === "facet-drawer") dialog.setAttribute("role", "dialog");
    try {
      dialog.showModal();
    } catch (e) {
      // iOS Safari can throw on showModal() (e.g. dialog under a transformed/
      // sticky ancestor). Fall back to an explicitly managed modal state.
      dialog.setAttribute("open", "");
      dialog.classList.add("dialog--fallback");
      activateDialogFallback(dialog);
    }
    document.documentElement.style.overflow = "hidden";
    var focusable = dialog.querySelector("[autofocus], [data-drawer-close], [data-lightbox-close]");
    if (!focusable && dialog.classList.contains("dialog--fallback")) focusable = dialog;
    if (focusable) focusable.focus();
  }

  function closeDialog(dialog) {
    if (!dialog || !dialog.open) return;
    dialog.close();
  }

  document.addEventListener("click", function (event) {
    var opener = event.target.closest("[data-drawer-open]");
    if (opener) {
      event.preventDefault();
      var target = opener.getAttribute("data-drawer-open");
      openDialog(document.getElementById(target), opener);
      if (target === CART_DRAWER_ID) loadCartUpsell();
      return;
    }
    var closer = event.target.closest("[data-drawer-close]");
    if (closer) {
      event.preventDefault();
      closeDialog(closer.closest("dialog"));
    }
  });

  // Restore scroll + return focus whenever any dialog closes; backdrop click closes.
  // This initializer is reusable because the theme editor and variant picker can
  // replace sections after the initial document load.
  function initDialogs(root) {
    var dialogs = Array.prototype.slice.call(root.querySelectorAll("dialog"));
    if (root.matches && root.matches("dialog")) dialogs.unshift(root);
    dialogs.forEach(function (dialog) {
      if (dialog.hasAttribute("data-dialog-initialized")) return;
      dialog.setAttribute("data-dialog-initialized", "true");
      dialog.addEventListener("close", function () {
        dialog.querySelectorAll("[data-compact-media-gallery]").forEach(stopMedia);
        cleanupDialogFallback(dialog);
        document.documentElement.style.overflow = "";
        if (dialog.id === "facet-drawer") dialog.removeAttribute("role");
        var invoker = dialogInvoker.get(dialog);
        dialogInvoker.delete(dialog);
        if (invoker && typeof invoker.focus === "function" && document.contains(invoker)) {
          invoker.focus();
        }
      });
      dialog.addEventListener("click", function (event) {
        if (event.target === dialog) closeDialog(dialog);
      });
    });
  }
  initDialogs(document);

  /* ---------- Predictive search ---------- */
  function initPredictiveSearch(root) {
    root
      .querySelectorAll("[data-predictive-input]:not([data-predictive-initialized])")
      .forEach(function (input) {
        var scope = input.closest("dialog") || document;
        var results = scope.querySelector("[data-predictive-results]");
        if (!results) return;
        input.setAttribute("data-predictive-initialized", "true");
        var endpoint =
          input.getAttribute("data-predictive-url") ||
          window.Shopify.routes.root + "search/suggest";
        var status = scope.querySelector("[data-predictive-status]");
        var timer;
        var controller;
        var requestId = 0;

        function render(term) {
          if (term.length < 2) {
            requestId += 1;
            if (controller) controller.abort();
            controller = null;
            results.innerHTML = "";
            if (status) status.textContent = "";
            return;
          }
          if (controller) controller.abort();
          var requestController = new AbortController();
          var currentRequestId = (requestId += 1);
          controller = requestController;
          var url =
            endpoint +
            "?q=" +
            encodeURIComponent(term) +
            "&section_id=predictive-search" +
            "&resources[type]=product,query&resources[limit]=6";
          fetch(url, { signal: requestController.signal })
            .then(function (response) {
              if (!response.ok) throw new Error("Predictive search request failed");
              return response.text();
            })
            .then(function (html) {
              if (currentRequestId !== requestId) return;
              var doc = new DOMParser().parseFromString(html, "text/html");
              var section = doc.querySelector("#shopify-section-predictive-search");
              results.innerHTML = section ? section.innerHTML : "";
              // Announce a count, not the whole result DOM, on each keystroke.
              // Count only result anchors — counting wrapper <li>s too would double it.
              var count = results.querySelectorAll(
                ".predictive__product, .predictive__query",
              ).length;
              if (status) {
                var s = window.CiteraStrings || {};
                var tpl =
                  count === 1
                    ? s.searchResultsOne || "[count] result"
                    : s.searchResultsOther || "[count] results";
                status.textContent = tpl.replace("[count]", count);
              }
            })
            .catch(function (error) {
              if (error && error.name === "AbortError") return;
              if (currentRequestId !== requestId) return;
              results.innerHTML = "";
              if (status) status.textContent = "";
            })
            .finally(function () {
              if (currentRequestId === requestId) controller = null;
            });
        }

        input.addEventListener("input", function () {
          var term = input.value.trim();
          clearTimeout(timer);
          if (term.length < 2) {
            render(term);
            return;
          }
          timer = setTimeout(function () {
            render(term);
          }, 220);
        });
      });
  }
  initPredictiveSearch(document);

  /* ---------- AJAX cart ---------- */
  function renderCartCount(count) {
    document.querySelectorAll("[data-cart-count]").forEach(function (el) {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }

  function findCartDrawerFocusTarget(root, focusState) {
    if (!root || !focusState || !focusState.line) return null;
    var controls = Array.prototype.slice.call(root.querySelectorAll("[data-cart-focus-action]"));
    var line = String(focusState.line);
    var previousLine = String(Math.max(1, Number(focusState.line) - 1));
    var action = focusState.action || "quantity";
    var exact = controls.find(function (control) {
      return (
        control.getAttribute("data-cart-line-key") === focusState.key &&
        control.getAttribute("data-cart-focus-action") === action
      );
    });
    if (exact) return exact;

    // After removal the next item takes the removed line's index; when the
    // removed line was last, use the same action on the previous item.
    return (
      controls.find(function (control) {
        return (
          control.getAttribute("data-line") === line &&
          control.getAttribute("data-cart-focus-action") === action
        );
      }) ||
      controls.find(function (control) {
        return (
          control.getAttribute("data-line") === previousLine &&
          control.getAttribute("data-cart-focus-action") === action
        );
      }) ||
      controls.find(function (control) {
        return (
          control.getAttribute("data-line") === line &&
          control.getAttribute("data-cart-focus-action") === "quantity"
        );
      }) ||
      controls.find(function (control) {
        return (
          control.getAttribute("data-line") === previousLine &&
          control.getAttribute("data-cart-focus-action") === "quantity"
        );
      }) ||
      null
    );
  }

  function restoreCartDrawerFocus(root, focusState) {
    if (!root || !focusState) return;
    var target = findCartDrawerFocusTarget(root, focusState);
    if (!target) {
      var drawer = root.closest("dialog");
      target = drawer && drawer.querySelector("[data-drawer-close]");
    }
    if (target && typeof target.focus === "function") target.focus();
  }

  function cartDrawerFocusState(control) {
    var source = control;
    var lineKey = control.getAttribute("data-cart-line-key");
    var active = document.activeElement;
    var activeControl = active && active.closest && active.closest("[data-cart-focus-action]");
    if (activeControl && activeControl.getAttribute("data-cart-line-key") === lineKey) {
      source = activeControl;
    }
    var state = {
      key: lineKey,
      line: Number(control.getAttribute("data-line")),
      action: source.getAttribute("data-cart-focus-action") || "quantity",
    };
    return state;
  }

  // Section Rendering replaces app-adjusted prices with Shopify base prices.
  // Ask Easify to render its own totals again; never parse option labels or
  // duplicate its pricing/discount/currency logic in the theme.
  function refreshEasifyCartPrices() {
    if (typeof window.easifyHandleCalculateCart !== "function") return;
    Promise.resolve()
      .then(function () { return window.easifyHandleCalculateCart(); })
      .catch(function (error) { console.warn("Easify cart price refresh failed", error); });
  }

  function refreshCartDrawer(sectionsHTML, focusState) {
    var current = document.getElementById(CART_CONTENT_ID);
    var html = sectionsHTML && sectionsHTML[CART_DRAWER_ID];
    var countRendered = false;
    var contentRendered = false;
    if (current && html) {
      var parsed = new DOMParser().parseFromString(html, "text/html");
      var nextCount = parsed.querySelector("[data-cart-count]");
      if (nextCount) {
        renderCartCount(Number(nextCount.textContent.trim()) || 0);
        countRendered = true;
      }
      var next = parsed.getElementById(CART_CONTENT_ID);
      if (next) {
        current.replaceWith(next); // swap inner region only, keep <dialog> open
        contentRendered = true;
        restoreCartDrawerFocus(next, focusState);
      }
    }
    if (!contentRendered) {
      // Shopify can commit the cart mutation but return null section HTML.
      // Navigate to the confirmed cart without repeating the add/change request.
      window.location.assign(window.Shopify.routes.root + "cart");
      return false;
    }
    if (!countRendered) updateCartCount();
    refreshEasifyCartPrices();
    loadCartUpsell();
    return true;
  }

  // In-cart discovery: fetch Shopify's native related recommendations for the
  // first line item. Complementary recommendations remain available on PDPs
  // where merchants intentionally configure them in Search & Discovery.
  function loadCartUpsell() {
    var box = document.querySelector("[data-cart-upsell]");
    if (!box) return;
    var pid = box.getAttribute("data-product-id");
    if (!pid) {
      box.innerHTML = "";
      return;
    }
    fetch(
      window.Shopify.routes.root +
        "recommendations/products?product_id=" +
        pid +
        "&limit=2&intent=related&section_id=cart-upsell",
    )
      .then(function (r) {
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, "text/html");
        var section = doc.querySelector(".shopify-section");
        box.innerHTML = section ? section.innerHTML : "";
      })
      .catch(function () {});
  }

  // Cart note + gift attribute (persist without re-rendering the open drawer).
  function updateCartField(body) {
    return fetch(window.Shopify.routes.root + "cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body),
    }).then(function (response) {
      if (!response.ok) throw new Error("Cart field update failed");
      return response.json();
    });
  }
  document.addEventListener("change", function (event) {
    var note = event.target.closest("[data-cart-note]");
    if (note) {
      var previousNote = note.hasAttribute("data-cart-confirmed-note")
        ? note.getAttribute("data-cart-confirmed-note")
        : note.defaultValue;
      var nextNote = note.value;
      note.disabled = true;
      note.setAttribute("aria-busy", "true");
      updateCartField({ note: nextNote })
        .then(function () {
          note.setAttribute("data-cart-confirmed-note", nextNote);
          announce(strings.cartUpdated || "Cart updated");
        })
        .catch(function () {
          if (note.isConnected) note.value = previousNote;
          announce(strings.cartError || "Couldn't update the cart");
        })
        .finally(function () {
          if (!note.isConnected) return;
          note.disabled = false;
          note.removeAttribute("aria-busy");
        });
      return;
    }
    var gift = event.target.closest("[data-cart-gift]");
    if (gift) {
      var previousGift = gift.hasAttribute("data-cart-confirmed-gift")
        ? gift.getAttribute("data-cart-confirmed-gift") === "true"
        : gift.defaultChecked;
      var nextGift = gift.checked;
      gift.disabled = true;
      gift.setAttribute("aria-busy", "true");
      updateCartField({ attributes: { Gift: nextGift ? "Yes" : "" } })
        .then(function () {
          gift.setAttribute("data-cart-confirmed-gift", nextGift ? "true" : "false");
          announce(strings.cartUpdated || "Cart updated");
        })
        .catch(function () {
          if (gift.isConnected) gift.checked = previousGift;
          announce(strings.cartError || "Couldn't update the cart");
        })
        .finally(function () {
          if (!gift.isConnected) return;
          gift.disabled = false;
          gift.removeAttribute("aria-busy");
        });
    }
  });

  function updateCartCount() {
    return fetch(window.Shopify.routes.root + "cart.js")
      .then(function (r) {
        if (!r.ok) throw new Error("Cart count request failed");
        return r.json();
      })
      .then(function (cart) {
        if (!cart || !Number.isFinite(Number(cart.item_count))) {
          throw new Error("Cart count response was invalid");
        }
        renderCartCount(cart.item_count);
      })
      .catch(function () {
        announce(strings.cartError || "Couldn't update the cart");
      });
  }

  function readCartResponse(response, requireAddedItems) {
    return response.json().then(function (data) {
      if (!response.ok || (data && (data.status || data.errors))) {
        var message =
          data &&
          (data.description || data.message || (typeof data.errors === "string" && data.errors));
        var error = new Error(message || strings.cartError || "Couldn't update the cart");
        error.cartMessage = message;
        throw error;
      }
      // Shopify multipart add returns one line; JSON batch add returns items.
      if (requireAddedItems && data && data.id && data.key && !Array.isArray(data.items)) {
        data.items = [{ id: data.id, key: data.key, quantity: data.quantity }];
      }
      if (!data || !Array.isArray(data.items) || (requireAddedItems && data.items.length === 0)) {
        throw new Error("Cart response was invalid");
      }
      return data;
    });
  }

  function cartChangeBody(control, quantity) {
    var key = control.getAttribute("data-cart-line-key");
    var body = { quantity: quantity };
    if (key) body.id = key;
    else body.line = Number(control.getAttribute("data-line"));
    return body;
  }

  function postCart(endpoint, body) {
    body.sections = [CART_DRAWER_ID];
    body.sections_url = window.location.pathname;
    return fetch(window.Shopify.routes.root + endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body),
    }).then(function (response) {
      return readCartResponse(response, endpoint === "cart/add.js");
    });
  }

  function quantityRuleForInput(input) {
    var min = Number(input.getAttribute("data-min") || 1);
    var maxAttribute = input.getAttribute("max");
    var max = maxAttribute === null || maxAttribute === "" ? null : Number(maxAttribute);
    var increment = Number(input.getAttribute("step") || 1);
    if (!Number.isInteger(min) || min < 1) min = 1;
    if (!Number.isInteger(max) || max < min) max = null;
    if (!Number.isInteger(increment) || increment < 1) increment = 1;
    return { min: min, max: max, increment: increment };
  }

  function previousQuantityValue(input) {
    var previousAttribute = input.getAttribute("data-previous-value");
    var raw =
      previousAttribute === null || previousAttribute === ""
        ? input.defaultValue
        : previousAttribute;
    if (String(raw).trim() === "") return quantityRuleForInput(input).min;
    var previous = Number(raw);
    return Number.isInteger(previous) && previous >= 0 ? previous : quantityRuleForInput(input).min;
  }

  function readCartQuantity(input) {
    var raw = String(input.value).trim();
    if (!raw) return null;
    var quantity = Number(raw);
    if (!Number.isFinite(quantity) || !Number.isInteger(quantity) || quantity < 0) return null;
    if (quantity === 0) return 0; // Zero is reserved for the explicit cart remove path.

    var rule = quantityRuleForInput(input);
    if (quantity < rule.min) return null;
    if (rule.max !== null && quantity > rule.max) return null;
    if (quantity % rule.increment !== 0) return null;
    return quantity;
  }

  function setCartDrawerPending(pending) {
    var content = document.getElementById(CART_CONTENT_ID);
    if (!content) return;
    if (pending) content.setAttribute("aria-busy", "true");
    else content.removeAttribute("aria-busy");

    content
      .querySelectorAll("[data-cart-quantity], [data-qty-step], [data-cart-remove]")
      .forEach(function (control) {
        if (pending) {
          control.setAttribute(
            "data-cart-pending-was-disabled",
            control.disabled ? "true" : "false",
          );
          control.disabled = true;
          return;
        }
        if (control.getAttribute("data-cart-pending-was-disabled") === "false") {
          control.disabled = false;
        }
        control.removeAttribute("data-cart-pending-was-disabled");
      });
  }

  function queueCartDrawerUpdate(task) {
    var request = cartDrawerUpdateQueue
      .catch(function () {})
      .then(function () {
        setCartDrawerPending(true);
        return Promise.resolve()
          .then(task)
          .finally(function () {
            setCartDrawerPending(false);
          });
      });
    cartDrawerUpdateQueue = request.catch(function () {});
    return request;
  }

  // Inline cart error next to a form + a polite announcement.
  function showCartError(form, message) {
    var text = message || strings.cartError || "Sorry, this item couldn't be added to your cart.";
    var box = form.querySelector("[data-cart-error]");
    if (!box) {
      box = document.createElement("p");
      box.setAttribute("data-cart-error", "");
      box.setAttribute("role", "alert");
      box.className = "cart-error";
      form.appendChild(box);
    }
    box.textContent = text;
    announce(text);
  }
  function clearCartError(form) {
    var box = form.querySelector("[data-cart-error]");
    if (box) box.remove();
  }

  function showCartLineError(line, message) {
    var text = message || strings.cartError || "Couldn't update the cart";
    var input = document.querySelector('[data-cart-quantity][data-line="' + line + '"]');
    var item = input && input.closest(".cart-item");
    if (item) {
      var box = item.querySelector("[data-cart-line-error]");
      if (!box) {
        box = document.createElement("p");
        box.id = "CartDrawerLineError-" + line;
        box.className = "cart-error";
        box.setAttribute("data-cart-line-error", "");
        box.setAttribute("role", "alert");
        var controls = item.querySelector(".cart-item__controls");
        (controls || item).insertAdjacentElement("afterend", box);
      }
      box.textContent = text;
      input.setAttribute("aria-describedby", box.id);
    }
    announce(text);
  }

  function reloadCartDrawerWithError(line, message, focusState) {
    showCartLineError(line, message);
    return fetch(window.Shopify.routes.root + "cart?section_id=" + CART_DRAWER_ID)
      .then(function (response) {
        if (!response.ok) throw new Error("Cart section request failed");
        return response.text();
      })
      .then(function (html) {
        var sections = {};
        sections[CART_DRAWER_ID] = html;
        refreshCartDrawer(sections, focusState);
        showCartLineError(line, message);
      })
      .catch(function () {
        showCartLineError(line, message);
      });
  }

  // Build a cart item from the full form: id + quantity + selling_plan +
  // properties[...] (gift-card recipient etc). Dropping these silently was a
  // real bug — the payload must mirror what a native (non-AJAX) submit sends.
  function formToItem(formData) {
    var item = {
      id: formData.get("id"),
      quantity: Number(formData.get("quantity") || 1),
    };
    var plan = formData.get("selling_plan");
    if (plan) item.selling_plan = Number(plan);
    var props = {};
    formData.forEach(function (value, key) {
      var m = key.match(/^properties\[(.+)\]$/);
      if (m && value !== "") props[m[1]] = value;
    });
    if (Object.keys(props).length) item.properties = props;
    return item;
  }

  function setProductRequestPending(form, pending) {
    var product = form.closest("[data-product]");
    var scope = product || form;
    scope
      .querySelectorAll(
        '[data-add-button], [data-sticky-add], [data-buy-now], button[type="submit"], input[type="submit"], [data-dynamic-checkout] button',
      )
      .forEach(function (control) {
        if (pending) {
          if (!control.hasAttribute("data-product-request-was-disabled")) {
            control.setAttribute(
              "data-product-request-was-disabled",
              control.disabled ? "true" : "false",
            );
          }
          control.disabled = true;
          control.setAttribute("aria-busy", "true");
          return;
        }
        if (control.getAttribute("data-product-request-was-disabled") === "false") {
          control.disabled = false;
        }
        control.removeAttribute("data-product-request-was-disabled");
        control.removeAttribute("aria-busy");
      });
    if (pending) form.setAttribute("aria-busy", "true");
    else form.removeAttribute("aria-busy");
  }

  function beginProductRequest(form) {
    var product = form.closest("[data-product]");
    if (form._citeraCartRequestPending || (product && product._citeraVariantRequest)) return false;
    form._citeraCartRequestPending = true;
    setProductRequestPending(form, true);
    return true;
  }

  function endProductRequest(form) {
    form._citeraCartRequestPending = false;
    var product = form.closest("[data-product]");
    setProductRequestPending(form, !!(product && product._citeraVariantRequest));
  }

  function productFormIsValid(form) {
    if (typeof form.checkValidity !== "function" || form.checkValidity()) return true;
    if (typeof form.reportValidity === "function") form.reportValidity();
    return false;
  }

  // Intercept add-to-cart forms so the drawer opens instead of a full page load.
  document.addEventListener("submit", function (event) {
    var form = event.target.closest("form[data-cart-form]");
    if (!form || event.defaultPrevented) return;
    var cartDrawer = document.getElementById(CART_DRAWER_ID);
    if (cartDrawer && cartDrawer.getAttribute("data-cart-type") === "page") return;
    event.preventDefault();
    if (!productFormIsValid(form) || !beginProductRequest(form)) return;
    var button = event.submitter || form.querySelector('[type="submit"]');
    clearCartError(form);

    var formData = new FormData(form);
    // Multipart keeps app-owned properties and file inputs in Shopify's native
    // form shape; Easify's fetch integration expects this for custom options.
    formData.set("sections", CART_DRAWER_ID);
    formData.set("sections_url", window.location.pathname);
    fetch(window.Shopify.routes.root + "cart/add.js", {
      method: "POST",
      headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
      body: formData,
    }).then(function (response) { return readCartResponse(response, true); })
      .then(function (data) {
        if (data && (data.status || data.description)) {
          showCartError(form, data.description || data.message);
          return;
        }
        // Keep quick-view errors visible. Close it only after Shopify confirms
        // the item was added successfully.
        var quickView = form.closest("#quick-view");
        if (quickView) closeDialog(quickView);
        if (refreshCartDrawer(data.sections)) {
          openDialog(document.getElementById(CART_DRAWER_ID), button);
        }
        announce(strings.cartAdded || "Added to cart");
      })
      .catch(function (error) {
        showCartError(form, error && error.cartMessage);
      })
      .finally(function () {
        endProductRequest(form);
      });
  });

  // Quantity change + remove inside the drawer (delegated).
  document.addEventListener("change", function (event) {
    var input = event.target.closest("[data-cart-quantity]");
    if (!input) return;
    var line = Number(input.getAttribute("data-line"));
    var previousValue = previousQuantityValue(input);
    var quantity = readCartQuantity(input);
    if (quantity === null) {
      input.value = previousValue;
      return;
    }
    if (quantity === previousValue) return;
    var focusState = cartDrawerFocusState(input);
    queueCartDrawerUpdate(function () {
      return postCart("cart/change.js", cartChangeBody(input, quantity))
        .then(function (data) {
          if (data && data.status) {
            return reloadCartDrawerWithError(
              line,
              data.description || data.message || strings.cartError,
              focusState,
            );
          }
          refreshCartDrawer(data.sections, focusState);
          announce(strings.cartUpdated || "Cart updated");
        })
        .catch(function (error) {
          return reloadCartDrawerWithError(
            line,
            (error && error.cartMessage) || strings.cartError || "Couldn't update the cart",
            focusState,
          );
        });
    });
  });

  document.addEventListener("click", function (event) {
    var remove = event.target.closest("[data-cart-remove]");
    if (!remove) return;
    event.preventDefault();
    var line = Number(remove.getAttribute("data-line"));
    var focusState = cartDrawerFocusState(remove);
    queueCartDrawerUpdate(function () {
      return postCart("cart/change.js", cartChangeBody(remove, 0))
        .then(function (data) {
          if (data && data.status) {
            announce(data.description || data.message || strings.cartError);
            return;
          }
          refreshCartDrawer(data.sections, focusState);
          announce(strings.cartUpdated || "Cart updated");
        })
        .catch(function (error) {
          return reloadCartDrawerWithError(
            line,
            (error && error.cartMessage) || strings.cartError || "Couldn't update the cart",
            focusState,
          );
        });
    });
  });

  function showCartPageLineError(input, message) {
    var text = message || strings.cartError || "Couldn't update the cart";
    var line = input.closest(".cart-line");
    var error = line && line.querySelector("[data-cart-page-line-error]");
    if (error) {
      error.textContent = text;
      error.hidden = false;
      input.setAttribute("aria-invalid", "true");
    }
    announce(text);
  }

  function setCartPagePending(page, pending) {
    if (pending) page.setAttribute("aria-busy", "true");
    else page.removeAttribute("aria-busy");
    page
      .querySelectorAll('[data-cart-page-quantity], button[type="submit"]')
      .forEach(function (control) {
        if (pending) {
          control.setAttribute("data-cart-page-was-disabled", control.disabled ? "true" : "false");
          control.disabled = true;
        } else {
          if (control.getAttribute("data-cart-page-was-disabled") === "false")
            control.disabled = false;
          control.removeAttribute("data-cart-page-was-disabled");
        }
      });
  }

  function restoreCartPageState(page, replacement, input) {
    var note = page.querySelector('[name="note"]');
    var nextNote = replacement.querySelector('[name="note"]');
    if (note && nextNote) {
      nextNote.value = note.value;
      var noteDetails = note.closest("details");
      var nextDetails = nextNote.closest("details");
      if (noteDetails && nextDetails) nextDetails.open = noteDetails.open;
    }
    var key = input.getAttribute("data-cart-line-key");
    var line = Number(input.getAttribute("data-line"));
    var controls = Array.prototype.slice.call(
      replacement.querySelectorAll("[data-cart-page-quantity]"),
    );
    var target =
      controls.find(function (control) {
        return key && control.getAttribute("data-cart-line-key") === key;
      }) ||
      controls.find(function (control) {
        return Number(control.getAttribute("data-line")) === line;
      }) ||
      controls.find(function (control) {
        return Number(control.getAttribute("data-line")) === line - 1;
      }) ||
      replacement.querySelector("h1");
    if (target) {
      if (target.tagName === "H1") target.setAttribute("tabindex", "-1");
      target.focus();
    }
  }

  function updateCartPageQuantity(input) {
    var page = input.closest("[data-cart-page]");
    var sectionId = page && page.dataset.sectionId;
    var line = Number(input.dataset.line);
    if (!page || !sectionId || !line) return;
    if (page.getAttribute("aria-busy") === "true") {
      input.value = previousQuantityValue(input);
      return;
    }

    var previousValue = previousQuantityValue(input);
    var quantity = readCartQuantity(input);
    if (quantity === null) {
      input.value = previousValue;
      return;
    }
    if (quantity === previousValue) return;
    setCartPagePending(page, true);
    input.removeAttribute("aria-invalid");
    var existingError = input.closest(".cart-line").querySelector("[data-cart-page-line-error]");
    if (existingError) existingError.hidden = true;

    var body = cartChangeBody(input, quantity);
    body.sections = [sectionId];
    body.sections_url = window.location.pathname;
    fetch(window.Shopify.routes.root + "cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body),
    })
      .then(readCartResponse)
      .then(function (data) {
        if (data && (data.status || data.errors)) {
          input.value = previousValue;
          showCartPageLineError(
            input,
            data.description ||
              data.message ||
              (typeof data.errors === "string" ? data.errors : strings.cartError),
          );
          return;
        }

        var html = data && data.sections && data.sections[sectionId];
        if (!html) {
          window.location.reload();
          return;
        }
        var parsed = new DOMParser().parseFromString(html, "text/html");
        var replacement = parsed.querySelector("[data-cart-page]");
        if (!replacement) {
          window.location.reload();
          return;
        }
        page.replaceWith(replacement);
        refreshEasifyCartPrices();
        restoreCartPageState(page, replacement, input);
        if (Number.isFinite(Number(data.item_count))) renderCartCount(data.item_count);
        if (
          window.Shopify &&
          window.Shopify.PaymentButton &&
          typeof window.Shopify.PaymentButton.init === "function"
        ) {
          window.Shopify.PaymentButton.init();
        }
        announce(strings.cartUpdated || "Cart updated");
      })
      .catch(function (error) {
        input.value = previousValue;
        showCartPageLineError(
          input,
          (error && error.cartMessage) || strings.cartError || "Couldn't update the cart",
        );
      })
      .finally(function () {
        if (page.isConnected) setCartPagePending(page, false);
      });
  }

  document.addEventListener("change", function (event) {
    var input = event.target.closest("[data-cart-page-quantity]");
    if (input) updateCartPageQuantity(input);
  });

  document.addEventListener("click", function (event) {
    var remove = event.target.closest(".cart-line__remove");
    var page = remove && remove.closest("[data-cart-page]");
    if (page && page.getAttribute("aria-busy") === "true") event.preventDefault();
  });

  // Stepper +/- buttons: adjust the sibling quantity input, then fire its change.
  document.addEventListener("click", function (event) {
    var step = event.target.closest("[data-qty-step]");
    if (!step) return;
    event.preventDefault();
    var input = step.parentElement.querySelector("[data-cart-quantity]");
    if (!input) return;
    var rule = quantityRuleForInput(input);
    var current = readCartQuantity(input);
    if (current === null) {
      current = previousQuantityValue(input);
      input.value = current;
    }
    var direction = Number(step.getAttribute("data-qty-step"));
    var next = current + direction * rule.increment;
    if (direction < 0 && next < rule.min) next = 0;
    if (direction > 0 && current === 0) next = rule.min;
    if (rule.max !== null && next > rule.max) next = rule.max;
    if (next === current) return;
    input.value = next;
    input.dispatchEvent(new Event("change", { bubbles: true }));
  });

  /* ---------- Product: high-variant selection + media gallery ---------- */
  // High-variant products expose at most 250 entries through `product.variants`.
  // The picker therefore sends selected option-value IDs to Shopify and replaces
  // the server-rendered product section instead of embedding every variant.
  function selectedOptionValueIds(product) {
    return Array.prototype.map
      .call(product.querySelectorAll("[data-option-index]"), function (control) {
        var selected =
          control.tagName === "SELECT"
            ? control.options[control.selectedIndex]
            : control.querySelector("input:checked");
        return selected && selected.dataset.optionValueId;
      })
      .filter(Boolean);
  }

  function preserveProductFormState(form, replacement) {
    var nextForm = replacement.querySelector("form[data-cart-form]");
    if (!form || !nextForm) return;
    var quantity = form.querySelector('[name="quantity"]');
    var nextQuantity = nextForm.querySelector('[name="quantity"]');
    if (quantity && nextQuantity) {
      var fallbackQuantity = nextQuantity.value;
      nextQuantity.value = quantity.value;
      var nextValue = readCartQuantity(nextQuantity);
      if (nextValue === null || nextValue === 0) nextQuantity.value = fallbackQuantity;
    }
    var plan = form.querySelector("[data-selling-plan]");
    var nextPlan = nextForm.querySelector("[data-selling-plan]");
    if (plan && !plan.disabled && nextPlan) {
      var matchingPlan = Array.prototype.some.call(nextPlan.options, function (option) {
        return option.value === plan.value;
      });
      if (matchingPlan) {
        nextPlan.value = plan.value;
        nextPlan.disabled = false;
        nextPlan.hidden = false;
        nextForm.querySelectorAll('[name="purchase_option"]').forEach(function (option) {
          option.checked = option.value === "subscription";
        });
      }
    }
    var toggle = form.querySelector("[data-gift-toggle]");
    var nextToggle = nextForm.querySelector("[data-gift-toggle]");
    if (!toggle || !nextToggle) return;
    nextToggle.checked = toggle.checked;
    var nextFields = nextForm.querySelector("[data-gift-fields]");
    if (nextFields) {
      nextFields.hidden = !toggle.checked;
      nextFields.querySelectorAll("input, textarea").forEach(function (field) {
        field.disabled = !toggle.checked;
        var previous = Array.prototype.find.call(
          form.querySelectorAll("input, textarea"),
          function (candidate) {
            return field.name && field.name === candidate.name;
          },
        );
        if (previous && field.type !== "file") field.value = previous.value;
      });
    }
  }

  function renderProductSelection(control) {
    var product = control.closest("[data-product]");
    if (!product) return;
    var sectionId = product.dataset.sectionId;
    var selectedControl =
      control.tagName === "SELECT"
        ? control.options[control.selectedIndex]
        : control.querySelector("input:checked");
    if (!sectionId || !selectedControl) return;

    var productUrl = selectedControl.dataset.productUrl || product.dataset.productUrl;
    if (!productUrl) return;
    var optionValueIds = selectedOptionValueIds(product);
    var pageUrl = new URL(productUrl, window.location.origin);
    if (optionValueIds.length) pageUrl.searchParams.set("option_values", optionValueIds.join(","));
    else pageUrl.searchParams.delete("option_values");
    pageUrl.searchParams.delete("variant");
    var selectedPlan = product.querySelector("[data-selling-plan]");
    if (selectedPlan && !selectedPlan.disabled && selectedPlan.value) {
      pageUrl.searchParams.set("selling_plan", selectedPlan.value);
    } else {
      pageUrl.searchParams.delete("selling_plan");
    }

    var requestUrl = new URL(pageUrl.toString());
    requestUrl.searchParams.set("section_id", sectionId);
    var focusId = control.tagName === "SELECT" ? control.id : selectedControl.id;

    if (product._citeraVariantRequest) product._citeraVariantRequest.abort();
    var controller = new AbortController();
    product._citeraVariantRequest = controller;
    product.setAttribute("aria-busy", "true");
    var purchaseForm = product.querySelector("form[data-cart-form]");
    if (purchaseForm) setProductRequestPending(purchaseForm, true);

    fetch(requestUrl.toString(), { signal: controller.signal })
      .then(function (response) {
        if (!response.ok) throw new Error("Product section request failed");
        return response.text();
      })
      .then(function (responseText) {
        if (product._citeraVariantRequest !== controller) return;
        var parsed = new DOMParser().parseFromString(responseText, "text/html");
        var replacement = parsed.querySelector("[data-product]");
        if (!replacement) throw new Error("Product section response was empty");

        product.querySelectorAll("[data-sticky-atc]").forEach(function (sticky) {
          (sticky._citeraObservers || []).forEach(function (observer) {
            observer.disconnect();
          });
        });
        preserveProductFormState(purchaseForm, replacement);
        // RoHandWork: Easify owns asynchronous fields and listeners on the
        // existing app block and purchase form. Keep those nodes on same-product
        // variant changes; update the native variant/availability from Shopify.
        var easify = product.querySelector('.easify-product-options');
        var nextPurchaseForm = replacement.querySelector('form[data-cart-form]');
        if (easify && purchaseForm && nextPurchaseForm &&
            product.dataset.productUrl === replacement.dataset.productUrl) {
          product.querySelectorAll('.shopify-app-block').forEach(function (appBlock) {
            var nextBlock = replacement.querySelector('[id="' + appBlock.id + '"]');
            if (nextBlock) nextBlock.replaceWith(appBlock);
          });
          setProductRequestPending(purchaseForm, false);
          var oldVariant = purchaseForm.querySelector('[name="id"]');
          var nextVariant = nextPurchaseForm.querySelector('[name="id"]');
          if (oldVariant && nextVariant) {
            oldVariant.value = nextVariant.value;
            oldVariant.setAttribute('value', nextVariant.value);
          }
          var oldQuantity = purchaseForm.querySelector('[name="quantity"]');
          var nextQuantity = nextPurchaseForm.querySelector('[name="quantity"]');
          if (oldQuantity && nextQuantity) {
            ['min', 'max', 'step', 'data-min'].forEach(function (attr) {
              if (nextQuantity.hasAttribute(attr)) oldQuantity.setAttribute(attr, nextQuantity.getAttribute(attr));
              else oldQuantity.removeAttribute(attr);
            });
          }
          var oldButton = purchaseForm.querySelector('[data-add-button]');
          var nextButton = nextPurchaseForm.querySelector('[data-add-button]');
          if (oldButton && nextButton) {
            oldButton.disabled = nextButton.disabled;
            oldButton.innerHTML = nextButton.innerHTML;
          }
          nextPurchaseForm.replaceWith(purchaseForm);
        }
        product.replaceWith(replacement);
        replacement.dispatchEvent(new CustomEvent("citera:section:rerender", { bubbles: true }));
        initProductModels(replacement);
        initStickyAtc(replacement.parentElement || document);
        if (
          window.Shopify &&
          window.Shopify.PaymentButton &&
          typeof window.Shopify.PaymentButton.init === "function"
        ) {
          window.Shopify.PaymentButton.init();
        }
        if (window.history && window.history.replaceState) {
          window.history.replaceState({}, "", pageUrl.toString());
        }
        if (focusId) {
          var nextFocus = document.getElementById(focusId);
          if (nextFocus) nextFocus.focus();
        }
      })
      .catch(function (error) {
        if ((error && error.name === "AbortError") || product._citeraVariantRequest !== controller)
          return;
        // A full navigation keeps option selection usable during a transient
        // Section Rendering API failure.
        window.location.assign(pageUrl.toString());
      })
      .finally(function () {
        if (product._citeraVariantRequest !== controller) return;
        product._citeraVariantRequest = null;
        if (product.isConnected) {
          product.removeAttribute("aria-busy");
          if (purchaseForm)
            setProductRequestPending(purchaseForm, !!purchaseForm._citeraCartRequestPending);
        }
      });
  }

  function selectedFeaturedOptionValueIds(featured) {
    return Array.prototype.map
      .call(featured.querySelectorAll("[data-featured-option-index]"), function (select) {
        var selected = select.options[select.selectedIndex];
        return selected && selected.dataset.optionValueId;
      })
      .filter(Boolean);
  }

  function renderFeaturedSelection(control) {
    var featured = control.closest("[data-featured-product]");
    if (!featured) return;
    var sectionId = featured.dataset.sectionId;
    var selectedControl = control.options[control.selectedIndex];
    var productUrl =
      (selectedControl && selectedControl.dataset.productUrl) || featured.dataset.productUrl;
    if (!sectionId || !selectedControl || !productUrl) return;

    var pageUrl = new URL(productUrl, window.location.origin);
    var optionValueIds = selectedFeaturedOptionValueIds(featured);
    if (optionValueIds.length) pageUrl.searchParams.set("option_values", optionValueIds.join(","));
    else pageUrl.searchParams.delete("option_values");
    pageUrl.searchParams.delete("variant");

    var requestUrl = new URL(pageUrl.toString());
    requestUrl.searchParams.set("section_id", sectionId);
    var focusId = control.id;

    if (featured._citeraVariantRequest) featured._citeraVariantRequest.abort();
    var controller = new AbortController();
    featured._citeraVariantRequest = controller;
    featured.setAttribute("aria-busy", "true");

    fetch(requestUrl.toString(), { signal: controller.signal })
      .then(function (response) {
        if (!response.ok) throw new Error("Featured product section request failed");
        return response.text();
      })
      .then(function (responseText) {
        var parsed = new DOMParser().parseFromString(responseText, "text/html");
        var replacement = parsed.querySelector("[data-featured-product]");
        if (!replacement) throw new Error("Featured product section response was empty");
        featured.replaceWith(replacement);
        replacement.dispatchEvent(new CustomEvent("citera:section:rerender", { bubbles: true }));
        if (
          window.Shopify &&
          window.Shopify.PaymentButton &&
          typeof window.Shopify.PaymentButton.init === "function"
        ) {
          window.Shopify.PaymentButton.init();
        }
        if (focusId) {
          var nextFocus = document.getElementById(focusId);
          if (nextFocus) nextFocus.focus();
        }
      })
      .catch(function (error) {
        if (error && error.name === "AbortError") return;
        window.location.assign(pageUrl.toString());
      })
      .finally(function () {
        if (featured.isConnected) featured.removeAttribute("aria-busy");
      });
  }

  document.addEventListener("change", function (event) {
    var control = event.target.closest("[data-featured-option-index]");
    if (control) renderFeaturedSelection(control);
  });

  // Purchase options: toggle the subscription plan select on/off. When one-time is
  // chosen the select is disabled so `selling_plan` is not submitted at all.
  document.addEventListener("change", function (event) {
    if (!event.target.closest("[data-purchase-options]")) return;
    if (event.target.name !== "purchase_option") return;
    var fieldset = event.target.closest("[data-purchase-options]");
    var planSelect = fieldset.querySelector("[data-selling-plan]");
    if (!planSelect) return;
    var isSub = event.target.value === "subscription";
    var hasPlans = planSelect.options.length > 0;
    planSelect.hidden = !isSub || !hasPlans;
    planSelect.disabled = !isSub || !hasPlans;
    if (hasPlans) renderProductSelection(planSelect);
  });

  document.addEventListener("change", function (event) {
    var plan = event.target.closest("[data-selling-plan]");
    if (plan) renderProductSelection(plan);
  });

  // Country / language selectors submit their localization form on change.
  document.addEventListener("change", function (event) {
    var loc = event.target.closest("[data-localization-select]");
    if (loc && loc.form) {
      loc.form.submit();
      return;
    }
  });

  // Gift-card recipient: reveal the fields + set the "send to recipient" flag.
  document.addEventListener("change", function (event) {
    var toggle = event.target.closest("[data-gift-toggle]");
    if (!toggle) return;
    var wrap = toggle.closest("[data-gift-recipient]");
    if (!wrap) return;
    var fields = wrap.querySelector("[data-gift-fields]");
    var flag = wrap.querySelector("[data-gift-enabled]");
    var offset = wrap.querySelector("[data-gift-offset]");
    if (fields) {
      fields.hidden = !toggle.checked;
      // Disabled inputs don't submit — so unchecking clears any typed recipient
      // properties from the add-to-cart payload.
      fields.querySelectorAll("input:not([data-gift-enabled]), textarea").forEach(function (el) {
        el.disabled = !toggle.checked;
      });
    }
    if (flag) flag.disabled = !toggle.checked;
    if (offset) {
      offset.value = String(new Date().getTimezoneOffset());
      offset.disabled = !toggle.checked;
    }
  });

  document.addEventListener("change", function (event) {
    var control = event.target.closest("[data-option-index]");
    if (!control) return;
    renderProductSelection(control);
  });

  function activateCompactMedia(gallery, mediaId) {
    if (!gallery || !mediaId) return;
    var target = gallery.querySelector(
      '[data-compact-media-item][data-media-id="' + mediaId + '"]',
    );
    if (!target) return;
    gallery.querySelectorAll("[data-compact-media-item]").forEach(function (item) {
      var active = item === target;
      var wasActive = !item.hidden;
      item.hidden = !active;
      if (!active && wasActive) stopMedia(item);
    });
    gallery.querySelectorAll("[data-compact-media-target]").forEach(function (button) {
      button.setAttribute(
        "aria-pressed",
        button.dataset.compactMediaTarget === String(mediaId) ? "true" : "false",
      );
    });
  }

  document.addEventListener("click", function (event) {
    var mediaButton = event.target.closest("[data-compact-media-target]");
    if (!mediaButton) return;
    activateCompactMedia(
      mediaButton.closest("[data-compact-media-gallery]"),
      mediaButton.dataset.compactMediaTarget,
    );
  });

  function activateThumb(product, thumb) {
    var main = product && product.querySelector("[data-media-main]");
    if (!main || !thumb) return;
    var width = Number(thumb.getAttribute("data-width"));
    var height = Number(thumb.getAttribute("data-height"));
    if (width > 0 && height > 0) {
      main.width = width;
      main.height = height;
    }
    main.src = thumb.getAttribute("data-full");
    main.srcset = thumb.getAttribute("data-srcset");
    var alt = thumb.getAttribute("data-alt");
    if (alt !== null) main.alt = alt;
    var focal = thumb.getAttribute("data-focal");
    main.style.objectPosition = focal ? focal : "";
    product.querySelectorAll("[data-thumb]").forEach(function (t) {
      t.setAttribute("aria-pressed", t === thumb ? "true" : "false");
    });
  }

  document.addEventListener("click", function (event) {
    var thumb = event.target.closest("[data-thumb]");
    if (!thumb) return;
    activateThumb(thumb.closest("[data-product]"), thumb);
  });

  // Pickup store information uses a native dialog with a non-modal fallback.
  document.addEventListener("click", function (event) {
    var open = event.target.closest("[data-pickup-open]");
    if (open) {
      var pickup = open.closest("[data-pickup]");
      var dialog = pickup && pickup.querySelector("[data-pickup-dialog]");
      if (!dialog) return;
      if (typeof dialog.showModal === "function") dialog.showModal();
      else dialog.setAttribute("open", "");
      var close = dialog.querySelector("[data-pickup-close]");
      if (close) close.focus();
      return;
    }

    var closeButton = event.target.closest("[data-pickup-close]");
    if (closeButton) {
      var closeDialog = closeButton.closest("[data-pickup-dialog]");
      if (!closeDialog) return;
      if (typeof closeDialog.close === "function") closeDialog.close();
      else closeDialog.removeAttribute("open");
    }
  });

  document.addEventListener("click", function (event) {
    var dialog = event.target.closest("[data-pickup-dialog]");
    if (dialog && event.target === dialog) dialog.close();
  });

  // Shopify XR powers the native "View in your space" button for product 3D
  // models. Models are embedded as JSON so this also works after editor reloads.
  function initProductModels(root) {
    root
      .querySelectorAll("[data-product-models]:not([data-xr-initialized])")
      .forEach(function (modelData) {
        modelData.setAttribute("data-xr-initialized", "true");
        if (!window.Shopify || typeof window.Shopify.loadFeatures !== "function") return;
        var models = [];
        try {
          models = JSON.parse(modelData.textContent);
        } catch (error) {
          return;
        }
        window.Shopify.loadFeatures([
          {
            name: "shopify-xr",
            version: "1.0",
            onLoad: function () {
              if (!window.ShopifyXR) return;
              window.ShopifyXR.addModels(models);
              window.ShopifyXR.setupXRElements();
            },
          },
        ]);
      });
  }

  /* ---------- Sticky add-to-cart: show when the main buy button scrolls away, hide over the footer ---------- */
  function initStickyAtc(root) {
    root.querySelectorAll("[data-product]").forEach(function (product) {
      var sticky = product.querySelector("[data-sticky-atc]");
      var anchor = product.querySelector("[data-add-button]");
      if (
        !sticky ||
        !anchor ||
        sticky.hasAttribute("data-sticky-initialized") ||
        !("IntersectionObserver" in window)
      )
        return;
      sticky.setAttribute("data-sticky-initialized", "true");
      var footer = document.querySelector(".footer");
      var pastAnchor = false;
      var footerVisible = false;
      var observers = [];
      function update() {
        sticky.classList.toggle("is-visible", pastAnchor && !footerVisible);
      }
      var anchorObserver = new IntersectionObserver(
        function (entries) {
          pastAnchor = entries[0].boundingClientRect.bottom < 0;
          update();
        },
        { rootMargin: "0px 0px -80px 0px" },
      );
      anchorObserver.observe(anchor);
      observers.push(anchorObserver);
      if (footer) {
        var footerObserver = new IntersectionObserver(
          function (entries) {
            footerVisible = entries[0].isIntersecting;
            update();
          },
          { rootMargin: "0px 0px 0px 0px" },
        );
        footerObserver.observe(footer);
        observers.push(footerObserver);
      }
      sticky._citeraObservers = observers;
    });
  }

  initProductModels(document);
  initStickyAtc(document);
  function reinitializeSection(event) {
    initDialogs(event.target);
    initPredictiveSearch(event.target);
    initProductModels(event.target);
    initStickyAtc(event.target);
    initProductRecommendations(event.target);
    initRecentlyViewed(event.target);
    initMarquees(event.target);
    initCountdowns(event.target);
    initStickyHeaders(event.target);
    initBackToTop(event.target);
  }
  document.addEventListener("shopify:section:load", reinitializeSection);
  document.addEventListener("citera:section:rerender", reinitializeSection);
  document.addEventListener("shopify:section:unload", function (event) {
    event.target.querySelectorAll("[data-sticky-atc]").forEach(function (sticky) {
      (sticky._citeraObservers || []).forEach(function (observer) {
        observer.disconnect();
      });
    });
    destroyCountdowns(event.target);
    destroyStickyHeaders(event.target);
    destroyBackToTop(event.target);
  });

  document.addEventListener("click", function (event) {
    var btn = event.target.closest("[data-sticky-add]");
    if (!btn) return;
    event.preventDefault();
    if (btn.disabled) return;
    var product = btn.closest("[data-product]");
    var form = product && product.querySelector("form[data-cart-form]");
    if (!form) return;
    if (form.requestSubmit) form.requestSubmit();
    else form.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
  });

  // Buy now: add the item, then go straight to checkout.
  document.addEventListener("click", function (event) {
    var btn = event.target.closest("[data-buy-now]");
    if (!btn) return;
    event.preventDefault();
    if (btn.disabled) return;
    var product = btn.closest("[data-product]");
    var form = product && product.querySelector("form[data-cart-form]");
    if (!form) return;
    if (!productFormIsValid(form) || !beginProductRequest(form)) return;
    clearCartError(form);
    var fd = new FormData(form);
    fetch(window.Shopify.routes.root + "cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items: [formToItem(fd)] }),
    })
      .then(function (response) {
        return readCartResponse(response, true);
      })
      .then(function (data) {
        if (data && (data.status || data.description)) {
          showCartError(form, data.description || data.message);
          return;
        }
        window.location.href = window.Shopify.routes.root + "checkout";
      })
      .catch(function (error) {
        showCartError(form, error && error.cartMessage);
      })
      .finally(function () {
        endProductRequest(form);
      });
  });

  /* ---------- Product recommendations (native Shopify API) ---------- */
  function initProductRecommendations(root) {
    root
      .querySelectorAll("[data-product-recs]:not([data-product-recs-initialized])")
      .forEach(function (el) {
        var url = el.getAttribute("data-url");
        if (!url) return;
        el.setAttribute("data-product-recs-initialized", "true");
        fetch(url)
          .then(function (r) {
            return r.text();
          })
          .then(function (html) {
            var parsed = new DOMParser().parseFromString(html, "text/html");
            var next = parsed.querySelector("[data-product-recs]");
            if (next) {
              next.setAttribute("data-product-recs-initialized", "true");
              el.replaceWith(next);
            }
          })
          .catch(function () {
            el.removeAttribute("data-product-recs-initialized");
          });
      });
  }
  initProductRecommendations(document);

  /* ---------- Recently viewed (localStorage, honest history) ---------- */
  var RECENT_KEY = "citera:recent";

  function readRecent() {
    try {
      return JSON.parse(localStorage.getItem(RECENT_KEY)) || [];
    } catch (e) {
      return [];
    }
  }

  function esc(s) {
    var d = document.createElement("div");
    d.textContent = s == null ? "" : s;
    return d.innerHTML;
  }

  function recordRecentItems(root) {
    findWithin(root, "[data-recent-item]").forEach(function (itemEl) {
      if (itemEl.hasAttribute("data-recent-initialized")) return;
      itemEl.setAttribute("data-recent-initialized", "true");
      try {
        var item = JSON.parse(itemEl.textContent);
        if (item && item.handle) {
          var list = readRecent().filter(function (p) {
            return p.handle !== item.handle;
          });
          list.unshift(item);
          localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 8)));
        }
      } catch (e) {}
    });
  }

  function renderRecentlyViewed(root) {
    findWithin(root, "[data-recently-viewed]").forEach(function (sec) {
      if (sec.hasAttribute("data-recent-initialized")) return;
      sec.setAttribute("data-recent-initialized", "true");
      var current = sec.getAttribute("data-current");
      var limit = Number(sec.getAttribute("data-limit")) || 4;
      var grid = sec.querySelector("[data-recently-viewed-grid]");
      if (!grid) return;
      var items = readRecent()
        .filter(function (p) {
          return p.handle !== current;
        })
        .slice(0, limit);
      if (!items.length) return;
      grid.innerHTML = items
        .map(function (p, index) {
          var imageWidth = Math.max(1, Math.round(Number(p.image_width) || 480));
          var imageHeight = Math.max(1, Math.round(Number(p.image_height) || 600));
          var img = p.image
            ? '<img src="' +
              esc(p.image) +
              '"' +
              (p.image_srcset ? ' srcset="' + esc(p.image_srcset) + '"' : "") +
              ' sizes="(min-width: 990px) 25vw, (min-width: 750px) 33vw, 50vw" width="' +
              imageWidth +
              '" height="' +
              imageHeight +
              '" alt="" loading="lazy" decoding="async">'
            : "";
          var ratio = /^(square|portrait|landscape|adapt)$/.test(p.ratio) ? p.ratio : "portrait";
          var price = p.on_sale
            ? '<span class="visually-hidden">' +
              esc(strings.salePrice || "Sale price") +
              '</span><span class="product-card__price-sale">' +
              esc(p.price) +
              '</span><span class="visually-hidden">' +
              esc(strings.regularPrice || "Regular price") +
              '</span><s class="product-card__price-compare">' +
              esc(p.compare_at_price) +
              "</s>"
            : esc(p.price);
          var vendor = p.vendor ? '<p class="product-card__vendor">' + esc(p.vendor) + "</p>" : "";
          var sequence = String(index + 1).padStart(2, "0");
          return (
            '<li><div class="product-card"><div class="product-card__sequence" aria-hidden="true"><span>' +
            sequence +
            '</span></div><a href="' +
            esc(p.url) +
            '" class="product-card__media product-card__media--' +
            ratio +
            '" tabindex="-1" aria-hidden="true">' +
            img +
            '</a><div class="product-card__info">' +
            vendor +
            '<div class="product-card__summary"><h3 class="product-card__title"><a href="' +
            esc(p.url) +
            '">' +
            esc(p.title) +
            '</a></h3><div class="product-card__price">' +
            price +
            "</div></div></div></div></li>"
          );
        })
        .join("");
      sec.hidden = items.length === 0;
    });
  }

  function initRecentlyViewed(root) {
    recordRecentItems(root);
    renderRecentlyViewed(root);
  }

  initRecentlyViewed(document);

  /* ---------- Marquee pause/play (WCAG 2.2.2) ---------- */
  document.addEventListener("click", function (event) {
    var toggle = event.target.closest("[data-marquee-toggle]");
    if (!toggle) return;
    var marquee = toggle.closest(".marquee");
    if (!marquee) return;
    var paused = marquee.classList.toggle("is-paused");
    toggle.setAttribute("aria-pressed", paused ? "true" : "false");
    var label = paused ? toggle.dataset.labelPlay : toggle.dataset.labelPause;
    if (label) toggle.setAttribute("aria-label", label);
  });

  /* ---------- Marquee: fill the viewport so the loop never leaves a gap ---------- */
  function fillMarquee(track) {
    // Drop clones from a previous run before remeasuring.
    track.querySelectorAll("[data-marquee-clone]").forEach(function (clone) {
      clone.remove();
    });
    var groups = track.querySelectorAll(".marquee__group");
    if (!groups.length) return;
    var first = groups[0];
    var groupWidth = first.getBoundingClientRect().width;
    var marquee = track.closest(".marquee");
    var viewport = (marquee || track).getBoundingClientRect().width;
    if (!groupWidth || !viewport) return;
    // Enough groups to always cover the viewport plus one to scroll into.
    var needed = Math.ceil(viewport / groupWidth) + 1;
    var count = track.querySelectorAll(".marquee__group").length;
    while (count < needed) {
      var clone = first.cloneNode(true);
      clone.setAttribute("inert", "");
      clone.setAttribute("aria-hidden", "true");
      clone.setAttribute("data-marquee-clone", "");
      track.appendChild(clone);
      count++;
    }
    // Shift by exactly one group width => a perfect loop at any width.
    track.style.setProperty("--marquee-shift", "-" + groupWidth + "px");
  }

  function initMarquees(root) {
    findWithin(root, ".marquee__track").forEach(fillMarquee);
  }

  initMarquees(document);
  var marqueeResizeTimer;
  window.addEventListener("resize", function () {
    clearTimeout(marqueeResizeTimer);
    marqueeResizeTimer = setTimeout(function () {
      initMarquees(document);
    }, 200);
  });

  /* ---------- Countdown timers (real deadline, no evergreen) ---------- */
  function pad(n) {
    return n < 10 ? "0" + n : String(n);
  }

  function tickCountdown(el) {
    var end = el._citeraCountdownEnd;
    if (!Number.isFinite(end)) {
      end = new Date(el.getAttribute("data-countdown-end")).getTime();
      el._citeraCountdownEnd = end;
    }
    var remaining = end - Date.now();
    var units = el.querySelector("[data-countdown-units]");
    var expired = el.querySelector("[data-countdown-expired]");

    if (isNaN(end)) return true; // bad date -> stop, leave placeholders
    if (remaining <= 0) {
      if (units) units.hidden = true;
      if (expired) expired.hidden = false;
      return true; // done
    }

    var secs = Math.floor(remaining / 1000);
    var days = Math.floor(secs / 86400);
    var hours = Math.floor((secs % 86400) / 3600);
    var mins = Math.floor((secs % 3600) / 60);
    var s = secs % 60;
    set(el, "[data-countdown-days]", days);
    set(el, "[data-countdown-hours]", pad(hours));
    set(el, "[data-countdown-minutes]", pad(mins));
    set(el, "[data-countdown-seconds]", pad(s));
    return false;
  }

  function set(root, sel, val) {
    var node = root.querySelector(sel);
    if (node) node.textContent = val;
  }

  var countdowns = new Set();
  var countdownTimer;

  function runCountdowns() {
    countdowns.forEach(function (el) {
      if (!el.isConnected || tickCountdown(el)) countdowns.delete(el);
    });
    if (!countdowns.size && countdownTimer) {
      clearInterval(countdownTimer);
      countdownTimer = null;
    }
  }

  function initCountdowns(root) {
    findWithin(root, "[data-countdown]").forEach(function (el) {
      if (el.hasAttribute("data-countdown-initialized")) return;
      el.setAttribute("data-countdown-initialized", "true");
      el._citeraCountdownEnd = new Date(el.getAttribute("data-countdown-end")).getTime();
      countdowns.add(el);
    });
    runCountdowns();
    if (countdowns.size && !countdownTimer) countdownTimer = setInterval(runCountdowns, 1000);
  }

  function destroyCountdowns(root) {
    findWithin(root, "[data-countdown]").forEach(function (el) {
      countdowns.delete(el);
      delete el._citeraCountdownEnd;
    });
    if (!countdowns.size && countdownTimer) {
      clearInterval(countdownTimer);
      countdownTimer = null;
    }
  }

  initCountdowns(document);

  /* ---------- Quick view (Section Rendering API into a modal) ---------- */
  var qvDialog = document.getElementById("quick-view");
  if (qvDialog) {
    var qvContent = qvDialog.querySelector("[data-quick-view-content]");
    var qvController;
    var qvRequestId = 0;
    document.addEventListener("click", function (event) {
      var btn = event.target.closest("[data-quick-view]");
      if (!btn) return;
      event.preventDefault();
      var handle = btn.getAttribute("data-quick-view");
      if (qvController) qvController.abort();
      var requestController = new AbortController();
      var currentRequestId = (qvRequestId += 1);
      qvController = requestController;
      qvContent.innerHTML = "";
      openDialog(qvDialog, btn);
      fetch(window.Shopify.routes.root + "products/" + handle + "?section_id=quick-view", {
        signal: requestController.signal,
      })
        .then(function (r) {
          if (!r.ok) throw new Error("Quick view request failed");
          return r.text();
        })
        .then(function (html) {
          if (currentRequestId !== qvRequestId) return;
          var doc = new DOMParser().parseFromString(html, "text/html");
          var inner = doc.querySelector(".qv");
          qvContent.innerHTML = inner ? inner.outerHTML : html;
          initProductModels(qvContent);
        })
        .catch(function (error) {
          if (error && error.name === "AbortError") return;
          if (currentRequestId !== qvRequestId) return;
          closeDialog(qvDialog);
        })
        .finally(function () {
          if (currentRequestId === qvRequestId) qvController = null;
        });
    });
    qvDialog.addEventListener("close", function () {
      if (!qvController) return;
      qvRequestId += 1;
      qvController.abort();
      qvController = null;
    });
  }

  /* ---------- Sticky header: fix to top on scroll, with a spacer so content doesn't jump ---------- */
  var stickyHeaderStates = new WeakMap();

  function initStickyHeaders(root) {
    findWithin(root, "[data-header-sticky]").forEach(function (stickyHeader) {
      if (stickyHeaderStates.has(stickyHeader)) return;
      var headerSpacer = document.createElement("div");
      headerSpacer.setAttribute("data-header-spacer", "");
      var state = {
        spacer: headerSpacer,
        stuck: false,
        baseTop: stickyHeader.getBoundingClientRect().top + window.scrollY,
      };
      var updateStuck = function () {
        if (!state.stuck && window.scrollY > state.baseTop + 4) {
          headerSpacer.style.height = stickyHeader.offsetHeight + "px";
          if (stickyHeader.parentNode)
            stickyHeader.parentNode.insertBefore(headerSpacer, stickyHeader);
          stickyHeader.classList.add("is-stuck");
          state.stuck = true;
          document.documentElement.style.setProperty(
            "--header-height",
            stickyHeader.offsetHeight + "px",
          );
        } else if (state.stuck && window.scrollY <= state.baseTop) {
          stickyHeader.classList.remove("is-stuck");
          if (headerSpacer.parentNode) headerSpacer.parentNode.removeChild(headerSpacer);
          state.stuck = false;
          document.documentElement.style.removeProperty("--header-height");
        }
      };
      var updateBase = function () {
        if (!state.stuck) {
          state.baseTop = stickyHeader.getBoundingClientRect().top + window.scrollY;
        }
      };
      state.updateStuck = updateStuck;
      state.updateBase = updateBase;
      stickyHeaderStates.set(stickyHeader, state);
      updateStuck();
      window.addEventListener("scroll", updateStuck, { passive: true });
      window.addEventListener("resize", updateBase, { passive: true });
    });
  }

  function destroyStickyHeaders(root) {
    findWithin(root, "[data-header-sticky]").forEach(function (stickyHeader) {
      var state = stickyHeaderStates.get(stickyHeader);
      if (!state) return;
      window.removeEventListener("scroll", state.updateStuck);
      window.removeEventListener("resize", state.updateBase);
      stickyHeader.classList.remove("is-stuck");
      if (state.spacer.parentNode) state.spacer.parentNode.removeChild(state.spacer);
      document.documentElement.style.removeProperty("--header-height");
      stickyHeaderStates.delete(stickyHeader);
    });
  }

  initStickyHeaders(document);

  /* ---------- Reveal sections on scroll (respects motion + reduced-motion) ---------- */
  var reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (
    document.body.classList.contains("anim-on") &&
    !reduceMotion &&
    "IntersectionObserver" in window
  ) {
    var reveals = Array.prototype.slice
      .call(document.querySelectorAll("#main-content > .shopify-section"))
      .filter(function (el) {
        // Never hide anything already in the first viewport (protects the LCP
        // section whatever its index — the homepage can lead with a marquee/bar).
        return el.getBoundingClientRect().top >= window.innerHeight;
      });
    reveals.forEach(function (el) {
      el.classList.add("reveal");
    });
    var revealObserver = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (e) {
          if (e.isIntersecting) {
            e.target.classList.add("reveal--in");
            obs.unobserve(e.target);
          }
        });
      },
      { rootMargin: "0px 0px -8% 0px" },
    );
    reveals.forEach(function (el) {
      revealObserver.observe(el);
    });
  }

  /* ---------- Faceted filters: submit on change ---------- */
  document.addEventListener("change", function (event) {
    var form = event.target.closest("[data-facets]");
    if (!form) return;
    if (!(event.target.matches("input") || event.target.matches("select"))) return;
    // Mobile filter drawer collects multiple selections, then submits via "Apply".
    // The sort form (and all desktop filtering) still auto-submits on change.
    if (
      form.hasAttribute("data-facets-filter") &&
      window.matchMedia("(max-width: 749px)").matches
    ) {
      return;
    }
    form.submit();
  });

  /* ---------- Mobile filter drawer: navigate on Apply ----------
     A <form> inside an open modal <dialog> does not reliably submit/navigate on
     iOS Safari. Build the GET URL from the form and navigate explicitly. Handle
     both the click (fires reliably on iOS) and submit (Android / Enter key). */
  function navigateFacets(form) {
    var params = new URLSearchParams();
    new FormData(form).forEach(function (value, key) {
      if (value !== "") params.append(key, value);
    });
    var query = params.toString();
    window.location.assign(query ? location.pathname + "?" + query : location.pathname);
  }
  document.addEventListener("click", function (event) {
    var btn = event.target.closest(".facet-drawer__apply, .facets__apply");
    if (!btn) return;
    var form = btn.closest("form[data-facets-filter]");
    if (!form) return;
    event.preventDefault();
    navigateFacets(form);
  });
  document.addEventListener("submit", function (event) {
    var form = event.target.closest("form[data-facets-filter]");
    if (!form) return;
    event.preventDefault();
    navigateFacets(form);
  });

  /* ---------- Faceted filters: only one dropdown open at a time ---------- */
  // The `toggle` event does not bubble, so listen in the capture phase.
  document.addEventListener(
    "toggle",
    function (event) {
      var facet = event.target;
      if (facet.tagName !== "DETAILS" || !facet.classList.contains("facet") || !facet.open) return;
      var bar = facet.closest(".facets__bar");
      if (!bar) return;
      bar.querySelectorAll("details.facet[open]").forEach(function (other) {
        if (other !== facet) other.open = false;
      });
    },
    true,
  );

  // Close any open facet when clicking outside the filter bar.
  document.addEventListener("click", function (event) {
    if (event.target.closest(".facet")) return;
    document.querySelectorAll("details.facet[open]").forEach(function (facet) {
      facet.open = false;
    });
  });

  /* ---------- Header dropdowns: only one open at a time ---------- */
  // Mirrors the facet handler above: opening a dropdown closes any sibling.
  // The `toggle` event does not bubble, so listen in the capture phase.
  document.addEventListener(
    "toggle",
    function (event) {
      var dd = event.target;
      if (dd.tagName !== "DETAILS" || !dd.classList.contains("header__dropdown") || !dd.open)
        return;
      document.querySelectorAll("details.header__dropdown[open]").forEach(function (other) {
        if (other !== dd) other.open = false;
      });
    },
    true,
  );

  /* ---------- Header dropdowns: dismiss on outside-click + Escape ---------- */
  document.addEventListener("click", function (event) {
    if (event.target.closest(".header__dropdown")) return;
    document.querySelectorAll("details.header__dropdown[open]").forEach(function (dd) {
      dd.open = false;
    });
  });
  document.addEventListener("keydown", function (event) {
    if (event.key !== "Escape") return;
    var open = document.querySelectorAll("details.header__dropdown[open]");
    if (!open.length) return;
    // Restore focus to the summary of the dropdown the user was inside, if any.
    var active = document.activeElement;
    var focusTarget = null;
    open.forEach(function (dd) {
      if (!focusTarget && dd.contains(active)) focusTarget = dd.querySelector("summary");
      dd.open = false;
    });
    if (focusTarget) focusTarget.focus();
  });

  /* ---------- Mobile navigation: one top-level group at a time ---------- */
  document.addEventListener(
    "click",
    function (event) {
      var summary = event.target.closest("#mobile-nav details.mobile-nav__disclosure > summary");
      if (!summary) return;
      var disclosure = summary.parentElement;
      var mobileNav = disclosure.closest("#mobile-nav");
      if (!mobileNav) return;
      mobileNav.querySelectorAll("details.mobile-nav__disclosure[open]").forEach(function (other) {
        if (other !== disclosure) other.open = false;
      });
    },
    true,
  );

  /* ---------- Mobile nav: close on resize to desktop ---------- */
  // At >=900px CSS hides the mobile-nav drawer with display:none, but the
  // <dialog> stays open so its scroll-lock (documentElement overflow:hidden)
  // is never released. Close it explicitly when the breakpoint is crossed.
  (function () {
    var desktop = window.matchMedia("(min-width: 900px)");
    function closeMobileNavOnDesktop(event) {
      if (!event.matches) return;
      var drawer = document.getElementById("mobile-nav");
      if (drawer && drawer.open) closeDialog(drawer);
    }
    if (desktop.addEventListener) desktop.addEventListener("change", closeMobileNavOnDesktop);
    else if (desktop.addListener) desktop.addListener(closeMobileNavOnDesktop);
  })();

  /* ---------- Customer accounts: recover toggle, address forms, country default ---------- */
  (function () {
    var recoverInvoker;
    function showRecover(name, invoker, moveFocus) {
      var recoverPanel = document.querySelector('[data-recover-panel="recover"]');
      document.querySelectorAll("[data-recover-panel]").forEach(function (panel) {
        panel.hidden = panel.getAttribute("data-recover-panel") !== name;
      });
      setPanelExpanded(recoverPanel, name === "recover");

      if (!moveFocus) return;
      if (name === "recover") {
        recoverInvoker = invoker;
        focusRevealedPanel(recoverPanel);
        return;
      }
      if (
        (!recoverInvoker || !document.contains(recoverInvoker)) &&
        document.querySelector("[data-recover-show]")
      ) {
        recoverInvoker = document.querySelector("[data-recover-show]");
      }
      if (recoverInvoker && typeof recoverInvoker.focus === "function") {
        recoverInvoker.focus();
      }
    }
    if (
      window.location.hash === "#recover" &&
      document.querySelector('[data-recover-panel="recover"]')
    ) {
      showRecover("recover", null, false);
    }

    document.addEventListener("click", function (event) {
      var recoverShow = event.target.closest("[data-recover-show]");
      if (recoverShow) return showRecover("recover", recoverShow, true);
      if (event.target.closest("[data-recover-hide]")) {
        return showRecover("login", null, true);
      }

      var newToggle = event.target.closest("[data-address-new-toggle]");
      if (newToggle) {
        var box = document.querySelector("[data-address-new]");
        toggleAddressPanel(box, newToggle);
        return;
      }
      var editToggle = event.target.closest("[data-address-edit-toggle]");
      if (editToggle) {
        var form = document.querySelector(
          '[data-address-edit="' + editToggle.getAttribute("data-address-edit-toggle") + '"]',
        );
        toggleAddressPanel(form, editToggle);
        return;
      }
      var del = event.target.closest("[data-address-delete]");
      if (del && del.dataset.confirm && !window.confirm(del.dataset.confirm)) {
        event.preventDefault();
      }
    });

    // Pre-select country selects from their data-default (no legacy Shopify lib).
    document.querySelectorAll("select[data-default]").forEach(function (select) {
      if (select.dataset.default) select.value = select.dataset.default;
    });
  })();

  /* ---------- Back to top ---------- */
  var backToTopStates = new WeakMap();

  function initBackToTop(root) {
    findWithin(root, "[data-back-to-top]").forEach(function (toTop) {
      if (backToTopStates.has(toTop)) return;
      toTop.setAttribute("data-back-to-top-initialized", "true");
      var onTopScroll = function () {
        toTop.hidden = window.scrollY < 600;
      };
      var onTopClick = function () {
        window.scrollTo({ top: 0, behavior: reduceMotion ? "auto" : "smooth" });
      };
      onTopScroll();
      window.addEventListener("scroll", onTopScroll, { passive: true });
      toTop.addEventListener("click", onTopClick);
      backToTopStates.set(toTop, { scroll: onTopScroll, click: onTopClick });
    });
  }

  function destroyBackToTop(root) {
    findWithin(root, "[data-back-to-top]").forEach(function (toTop) {
      var state = backToTopStates.get(toTop);
      if (!state) return;
      window.removeEventListener("scroll", state.scroll);
      toTop.removeEventListener("click", state.click);
      toTop.removeAttribute("data-back-to-top-initialized");
      backToTopStates.delete(toTop);
    });
  }

  initBackToTop(document);
})();
